import React from 'react';

import type { ExtractInjectionAPI, ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { AIButtonPluginState } from '../../pm-plugins/ai-button/ai-button-plugin';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { AtlassianIntelligenceToolbarButton } from '../components/AtlassianIntelligenceToolbarButton/AtlassianIntelligenceToolbarButton';

import { useIsAiEntryPointDisabled } from './utils/use-is-ai-entry-point-disabled';

type PrimaryToolbarComponentType = {
	aiButtonPluginState?: AIButtonPluginState;
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
};

const PrimaryToolbarComponent = ({
	editorView,
	api,
	editorPluginAIProvider,
	aiGlobalOptIn,
	disabled,
}: PrimaryToolbarComponentType) => {
	const aiButtonState = useSharedPluginStateSelector(api, 'aiExperience.aiButtonState');
	const isAiEntryPointDisabled = useIsAiEntryPointDisabled(api);

	const onClick = () => {
		const { state, dispatch } = editorView;
		const configItem = editorPluginAIProvider.baseGenerate;
		const openAIModalCommand = createOpenAIModalCommand({
			state,
			configItem,
			lastTriggeredFrom: 'mainToolbar',
			aiGlobalOptIn,
			triggeredFor: aiButtonState?.triggeredFor,
		});

		openAIModalCommand(state, dispatch);
	};

	return (
		<AtlassianIntelligenceToolbarButton
			api={api}
			ButtonWrapper={editorPluginAIProvider?.AIButtonWrapper}
			onClick={onClick}
			disabled={
				disabled || isAiEntryPointDisabled || !aiButtonState?.isSelectionValidForAIExperience
			}
			view={editorView}
		/>
	);
};

export const getAIPrimaryToolbarComponent =
	({
		api,
		editorPluginAIProvider,
		aiGlobalOptIn,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: ExtractInjectionAPI<AIPlugin>;
		editorPluginAIProvider: EditorPluginAIProvider;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				editorPluginAIProvider={editorPluginAIProvider}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
			/>
		);
	};
